import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { FileDown, ExternalLink } from 'lucide-react';

export default function PatientInvoices() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get('/invoices').then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const fetchPdfBlob = async (inv) => {
    const r = await api.get(`/invoices/${inv.invoice_id}/pdf`, { responseType: 'blob' });
    return new Blob([r.data], { type: 'application/pdf' });
  };

  const downloadPdf = async (inv) => {
    setBusy(`dl-${inv.invoice_id}`);
    try {
      const blob = await fetchPdfBlob(inv);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(inv.invoice_number || 'invoice').replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert('PDF download failed: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  const openPdf = async (inv) => {
    setBusy(`open-${inv.invoice_id}`);
    try {
      const blob = await fetchPdfBlob(inv);
      const url = URL.createObjectURL(blob);
      // Open in new tab — browser's native PDF viewer has Print built-in.
      const w = window.open(url, '_blank');
      if (!w) {
        // Pop-up blocked — fall back to same-tab nav.
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert('Open PDF failed: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">BILLING</div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">Invoices</h1>
        <p className="text-xs text-fg-muted mt-1">Tap <strong>Open</strong> to view the PDF in a new tab — you can print it from there.</p>
      </div>

      <div className="bento-card">
        <table>
          <thead><tr><th>Number</th><th>Issued</th><th>Total</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.invoice_id} data-testid={`invoice-${i.invoice_id}`}>
                <td className="font-mono text-xs">{i.invoice_number}</td>
                <td className="text-xs text-fg-muted">{i.issued_at?.slice(0,10)}</td>
                <td className="font-medium">{i.total.toFixed(2)} {i.currency}</td>
                <td><Status s={i.status} /></td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={()=>openPdf(i)} disabled={busy === `open-${i.invoice_id}`}
                            className="text-xs text-brand inline-flex items-center gap-1 hover:underline"
                            data-testid={`patient-open-${i.invoice_id}`}>
                      <ExternalLink className="w-3.5 h-3.5"/> {busy === `open-${i.invoice_id}` ? 'Opening…' : 'Open'}
                    </button>
                    <button onClick={()=>downloadPdf(i)} disabled={busy === `dl-${i.invoice_id}`}
                            className="text-xs text-brand inline-flex items-center gap-1 hover:underline"
                            data-testid={`patient-pdf-${i.invoice_id}`}>
                      <FileDown className="w-3.5 h-3.5"/> {busy === `dl-${i.invoice_id}` ? 'Saving…' : 'Download'}
                    </button>
                    {i.status !== 'paid' && (
                      <span className="text-xs text-fg-muted italic" title="Pay via BLIK or cash at the clinic">
                        Pay off-platform
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan="5" className="text-center text-fg-muted py-8">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Status({ s }) {
  const cls = s === 'paid' ? 'badge-success' : s === 'unpaid' ? 'badge-warn' : 'badge-error';
  return <span className={cls}>{s}</span>;
}
